import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Download, LogOut, MessageCircle, Plus, Star, Trash2 } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "In Progress", "Closed"];

const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err.response?.status === 401 && !err.config._retried && !err.config.url.includes("/auth/refresh")) {
      err.config._retried = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return api(err.config);
      } catch {
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

export default function Admin() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  if (user === null) {
    return <div className="admin-shell admin-loading" data-testid="admin-loading">Loading…</div>;
  }
  if (user === false) {
    return <LoginView onLogin={setUser} />;
  }
  return <Dashboard user={user} onLogout={() => setUser(false)} />;
}

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-shell login-shell">
      <form className="admin-login-card" data-testid="admin-login-form" onSubmit={submit}>
        <Logo />
        <h1>Owner login</h1>
        <p>View quote requests and manage customer reviews.</p>
        {error && <div className="form-error" data-testid="admin-login-error" role="alert">{error}</div>}
        <label>Email<input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@sherawali.com" /></label>
        <label>Password<input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></label>
        <button className="btn btn-dark form-submit" data-testid="admin-login-submit" type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
        <Link to="/" className="admin-back-link" data-testid="admin-back-home">← Back to website</Link>
      </form>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("leads");
  const [quotes, setQuotes] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [notice, setNotice] = useState("");

  const loadQuotes = useCallback(() => {
    api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  }, []);
  const loadTestimonials = useCallback(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadQuotes(); loadTestimonials(); }, [loadQuotes, loadTestimonials]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    onLogout();
  };

  const setStatus = async (id, status) => {
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    await api.patch(`/admin/quotes/${id}`, { status }).catch(() => loadQuotes());
  };

  const newCount = quotes.filter((q) => (q.status || "New") === "New").length;

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <header className="admin-topbar">
        <Logo />
        <div className="admin-topbar-right">
          <span data-testid="admin-user-email">{user.email}</span>
          <button className="btn btn-dark btn-sm" data-testid="admin-logout-button" onClick={logout}><LogOut size={14} /> Logout</button>
        </div>
      </header>

      <div className="admin-body">
        <div className="admin-stats">
          <div className="stat-card-admin" data-testid="stat-total-leads"><strong>{quotes.length}</strong><span>Total leads</span></div>
          <div className="stat-card-admin" data-testid="stat-new-leads"><strong>{newCount}</strong><span>New requests</span></div>
          <div className="stat-card-admin" data-testid="stat-reviews"><strong>{testimonials.length}</strong><span>Live reviews</span></div>
        </div>

        <div className="admin-tabs">
          <button data-testid="tab-leads" className={tab === "leads" ? "active" : ""} onClick={() => setTab("leads")}>Quote leads</button>
          <button data-testid="tab-reviews" className={tab === "reviews" ? "active" : ""} onClick={() => setTab("reviews")}>Reviews</button>
          <a className="btn btn-dark btn-sm export-link" data-testid="export-leads-button" href={`${API}/admin/quotes/export`} download>
            <Download size={14} /> Download leads (Excel)
          </a>
        </div>
        {notice && <div className="admin-notice" data-testid="admin-notice">{notice}</div>}

        {tab === "leads" ? (
          <LeadsTab quotes={quotes} setStatus={setStatus} />
        ) : (
          <ReviewsTab testimonials={testimonials} reload={loadTestimonials} setNotice={setNotice} />
        )}
      </div>
    </div>
  );
}

const waNumber = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.length === 10 ? `91${digits}` : digits;
};

const waLink = (q) => {
  const num = waNumber(q.phone);
  if (!num) return null;
  const msg = `Hi ${q.name}, this is Jay Maa Sherawali Packers and Movers, Patna. Thanks for your ${q.move_type} quote request (${q.from_location} to ${q.to_location}). When can we call you with your estimate?`;
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
};

function LeadsTab({ quotes, setStatus }) {
  if (!quotes.length) {
    return <div className="admin-empty" data-testid="leads-empty">No quote requests yet. New submissions from the website will appear here.</div>;
  }
  return (
    <div className="leads-list" data-testid="leads-list">
      {quotes.map((q) => (
        <div className="lead-row" data-testid={`lead-row-${q.id.slice(0, 8)}`} key={q.id}>
          <div className="lead-main">
            <strong>{q.name}</strong>
            <a href={`tel:${q.phone}`} data-testid={`lead-phone-${q.id.slice(0, 8)}`}>{q.phone}</a>
            <small>{q.from_location} → {q.to_location} · {q.move_type}{q.goods_volume ? ` · ${q.goods_volume}` : ""}{q.move_date ? ` · ${q.move_date}` : ""}</small>
            {q.message && <small className="lead-message">“{q.message}”</small>}
          </div>
          <div className="lead-side">
            <small>{fmtDate(q.created_at)}</small>
            <div className="lead-actions">
              {waLink(q) && (
                <a className="lead-wa" data-testid={`lead-whatsapp-${q.id.slice(0, 8)}`} href={waLink(q)} target="_blank" rel="noopener noreferrer">
                  <MessageCircle size={13} /> WhatsApp
                </a>
              )}
              <select data-testid={`lead-status-${q.id.slice(0, 8)}`} value={q.status || "New"} onChange={(e) => setStatus(q.id, e.target.value)} className={`status-select s-${(q.status || "New").toLowerCase().replace(/\s/g, "-")}`}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const emptyReview = { name: "", route: "", text: "", rating: 5 };

function ReviewsTab({ testimonials, reload, setNotice }) {
  const [drafts, setDrafts] = useState({});
  const [newReview, setNewReview] = useState(emptyReview);

  const draft = (t) => drafts[t.id] ?? t;
  const setDraft = (t, patch) => setDrafts((d) => ({ ...d, [t.id]: { ...draft(t), ...patch } }));

  const save = async (t) => {
    const d = draft(t);
    await api.put(`/admin/testimonials/${t.id}`, { name: d.name, route: d.route, text: d.text, rating: Number(d.rating) })
      .then(() => { setNotice("Review updated."); reload(); })
      .catch((e) => setNotice(formatApiErrorDetail(e.response?.data?.detail)));
    setTimeout(() => setNotice(""), 2500);
  };

  const remove = async (t) => {
    await api.delete(`/admin/testimonials/${t.id}`).then(reload).catch(() => {});
  };

  const add = async (e) => {
    e.preventDefault();
    await api.post("/admin/testimonials", { ...newReview, rating: Number(newReview.rating) })
      .then(() => { setNewReview(emptyReview); setNotice("Review added."); reload(); })
      .catch((err) => setNotice(formatApiErrorDetail(err.response?.data?.detail)));
    setTimeout(() => setNotice(""), 2500);
  };

  return (
    <div className="reviews-admin" data-testid="reviews-admin">
      {testimonials.map((t) => (
        <div className="review-edit-card" data-testid={`review-edit-${t.id.slice(0, 8)}`} key={t.id}>
          <div className="form-row">
            <label>Name<input data-testid={`review-name-${t.id.slice(0, 8)}`} value={draft(t).name} onChange={(e) => setDraft(t, { name: e.target.value })} /></label>
            <label>Move details<input data-testid={`review-route-${t.id.slice(0, 8)}`} value={draft(t).route} onChange={(e) => setDraft(t, { route: e.target.value })} placeholder="Home move · Patna to Pune" /></label>
          </div>
          <label>Review<textarea data-testid={`review-text-${t.id.slice(0, 8)}`} rows={3} value={draft(t).text} onChange={(e) => setDraft(t, { text: e.target.value })} /></label>
          <div className="review-edit-footer">
            <label className="rating-label"><Star size={13} /> Rating
              <select data-testid={`review-rating-${t.id.slice(0, 8)}`} value={draft(t).rating} onChange={(e) => setDraft(t, { rating: e.target.value })}>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <div>
              <button className="btn btn-dark btn-sm" data-testid={`review-save-${t.id.slice(0, 8)}`} onClick={() => save(t)}>Save</button>
              <button className="btn btn-danger btn-sm" data-testid={`review-delete-${t.id.slice(0, 8)}`} onClick={() => remove(t)}><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        </div>
      ))}

      <form className="review-edit-card new-review" data-testid="new-review-form" onSubmit={add}>
        <h3><Plus size={16} /> Add a new review</h3>
        <div className="form-row">
          <label>Name<input data-testid="new-review-name" required value={newReview.name} onChange={(e) => setNewReview({ ...newReview, name: e.target.value })} /></label>
          <label>Move details<input data-testid="new-review-route" value={newReview.route} onChange={(e) => setNewReview({ ...newReview, route: e.target.value })} placeholder="Car transport · Patna to Mumbai" /></label>
        </div>
        <label>Review<textarea data-testid="new-review-text" required rows={3} value={newReview.text} onChange={(e) => setNewReview({ ...newReview, text: e.target.value })} /></label>
        <div className="review-edit-footer">
          <label className="rating-label"><Star size={13} /> Rating
            <select data-testid="new-review-rating" value={newReview.rating} onChange={(e) => setNewReview({ ...newReview, rating: e.target.value })}>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button className="btn btn-gold btn-sm" data-testid="new-review-submit" type="submit">Add review</button>
        </div>
      </form>
    </div>
  );
}
